import { useEffect, useRef, useState } from 'react';

const prefersReduced = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export function useInView(options = {}) {
  const ref = useRef(null);
  const [inView, setInView] = useState(prefersReduced);

  useEffect(() => {
    if (prefersReduced()) {
      setInView(true);
      return;
    }
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.08, ...options }
    );

    observer.observe(el);
    return () => observer.disconnect();
  // options is intentionally omitted — callers pass static literals
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [ref, inView];
}

const HOVER_T = [
  'border-color 0.3s ease',
  'background-color 0.3s ease',
  'color 0.2s ease',
  'box-shadow 0.2s ease',
].join(', ');

export function useStagger({ count, stagger = 60, duration = 500, threshold = 0.08 } = {}) {
  const [ref, inView] = useInView({ threshold });
  const [done, setDone] = useState(prefersReduced);

  useEffect(() => {
    if (!inView || done) return;
    const t = setTimeout(
      () => setDone(true),
      (count - 1) * stagger + duration + 80
    );
    return () => clearTimeout(t);
  }, [inView, done, count, stagger, duration]);

  const itemStyle = (i) => ({
    opacity: inView ? 1 : 0,
    transform: inView ? 'translateY(0)' : 'translateY(18px)',
    transition: [
      `opacity ${duration}ms cubic-bezier(0.23,1,0.32,1) ${done ? 0 : i * stagger}ms`,
      `transform ${duration}ms cubic-bezier(0.23,1,0.32,1) ${done ? 0 : i * stagger}ms`,
      HOVER_T,
    ].join(', '),
  });

  return { ref, inView, done, itemStyle };
}
